import React, { useState, useEffect, useMemo } from 'react';
import { getTheme, useThemeMode } from './theme';
import { api } from './api';

export interface WizardData {
  fastighetsBeteckning: string; name: string; areaHa: string; county: string; municipality: string;
  gameTypes: string[]; difficulty: string; contactName: string; contactEmail: string; contactPhone: string;
  address: string; description: string; facilities: string[]; highlights: string;
  ownerType: 'person' | 'foretag'; ownerNumber: string; swishNumber: string; swishAmount: string; invoiceEmail: string;
}
export interface QidUser { id?: string; sub?: string; email: string; name?: string; picture?: string; is_admin?: boolean; }
interface ArrChatProps { user: QidUser | null; onOpenWizard?: (preData?: Partial<WizardData>) => void; isAdmin?: boolean; onMarkCreated?: () => void; }

type SpKey = 'alg' | 'kronhjort' | 'dovhjort' | 'radjur' | 'vildsvin' | 'mufflon' | 'fasan' | 'skogsfagel';

interface SubCatDef {
  key: string; label: string; pctOfHarvest: number;
  fallKrDefault: number; slaktKg: number;
}
interface SpeciesDef {
  key: SpKey; label: string; refPer100Ha: number; harvFrac: number;
  fallKrDefault: number; meatKgDefault: number;
}

const SPECIES: SpeciesDef[] = [
  { key: 'alg',        label: 'Älg',         refPer100Ha: 1.15, harvFrac: 0.12, fallKrDefault: 3200, meatKgDefault: 75 },
  { key: 'kronhjort',  label: 'Kronhjort',   refPer100Ha: 2.8,  harvFrac: 0.15, fallKrDefault: 4500, meatKgDefault: 80 },
  { key: 'dovhjort',   label: 'Dovhjort',    refPer100Ha: 5.5,  harvFrac: 0.18, fallKrDefault: 2800, meatKgDefault: 75 },
  { key: 'radjur',     label: 'Rådjur',      refPer100Ha: 22,   harvFrac: 0.22, fallKrDefault: 600,  meatKgDefault: 90 },
  { key: 'vildsvin',   label: 'Vildsvin',    refPer100Ha: 12,   harvFrac: 0.35, fallKrDefault: 400,  meatKgDefault: 65 },
  { key: 'mufflon',    label: 'Mufflon',     refPer100Ha: 3,    harvFrac: 0.15, fallKrDefault: 1800, meatKgDefault: 70 },
  { key: 'fasan',      label: 'Fasan / and', refPer100Ha: 35,   harvFrac: 0.28, fallKrDefault: 150,  meatKgDefault: 0  },
  { key: 'skogsfagel', label: 'Skogsfågel',  refPer100Ha: 18,   harvFrac: 0.20, fallKrDefault: 0,    meatKgDefault: 0  },
];

const SUBCATS: Partial<Record<SpKey, SubCatDef[]>> = {
  alg: [
    { key: 'alg_tjur_stor', label: 'Tjur >10 taggar',  pctOfHarvest: 0.15, fallKrDefault: 6500, slaktKg: 180 },
    { key: 'alg_tjur_4_10', label: 'Tjur 4–10 taggar', pctOfHarvest: 0.20, fallKrDefault: 4500, slaktKg: 150 },
    { key: 'alg_tjur_2_4',  label: 'Tjur 2–4 taggar',  pctOfHarvest: 0.15, fallKrDefault: 3000, slaktKg: 110 },
    { key: 'alg_kviga',     label: 'Kviga',             pctOfHarvest: 0.10, fallKrDefault: 2200, slaktKg: 90  },
    { key: 'alg_ko',        label: 'Ko',                pctOfHarvest: 0.25, fallKrDefault: 3000, slaktKg: 120 },
    { key: 'alg_kalv',      label: 'Kalv',              pctOfHarvest: 0.15, fallKrDefault: 1600, slaktKg: 60  },
  ],
  kronhjort: [
    { key: 'kron_kapital',  label: 'Kapitalhjort',        pctOfHarvest: 0.18, fallKrDefault: 8000, slaktKg: 115 },
    { key: 'kron_u10',      label: 'Hjort under 10 taggar', pctOfHarvest: 0.32, fallKrDefault: 5000, slaktKg: 70 },
    { key: 'kron_spets',    label: 'Spets',               pctOfHarvest: 0.20, fallKrDefault: 3000, slaktKg: 48  },
    { key: 'kron_hind',     label: 'Hind',                pctOfHarvest: 0.20, fallKrDefault: 2800, slaktKg: 55  },
    { key: 'kron_kalv',     label: 'Kalv',                pctOfHarvest: 0.10, fallKrDefault: 1800, slaktKg: 32  },
  ],
  dovhjort: [
    { key: 'dov_spets',      label: 'Dovspets',      pctOfHarvest: 0.12, fallKrDefault: 5500, slaktKg: 58 },
    { key: 'dov_helskovel',  label: 'Dovhelskovel',  pctOfHarvest: 0.12, fallKrDefault: 4200, slaktKg: 50 },
    { key: 'dov_halvskovel', label: 'Dovhalvskovel', pctOfHarvest: 0.16, fallKrDefault: 2800, slaktKg: 38 },
    { key: 'dov_hind',       label: 'Dovhind',       pctOfHarvest: 0.35, fallKrDefault: 1600, slaktKg: 40 },
    { key: 'dov_kalv',       label: 'Dovkalv',       pctOfHarvest: 0.25, fallKrDefault: 900,  slaktKg: 18 },
  ],
  radjur: [
    { key: 'ra_medalj',  label: 'Råbock medalj',    pctOfHarvest: 0.08, fallKrDefault: 1400, slaktKg: 14 },
    { key: 'ra_6tag',    label: 'Råbock 6-taggare', pctOfHarvest: 0.12, fallKrDefault: 950,  slaktKg: 12 },
    { key: 'ra_4tag',    label: 'Råbock 4-taggare', pctOfHarvest: 0.12, fallKrDefault: 700,  slaktKg: 11 },
    { key: 'ra_spets',   label: 'Råbock spets',     pctOfHarvest: 0.08, fallKrDefault: 450,  slaktKg: 9  },
    { key: 'ra_get',     label: 'Råget',            pctOfHarvest: 0.35, fallKrDefault: 500,  slaktKg: 11 },
    { key: 'ra_kid',     label: 'Råkid',            pctOfHarvest: 0.25, fallKrDefault: 280,  slaktKg: 7  },
  ],
  vildsvin: [
    { key: 'vds_sugga',   label: 'Sugga',    pctOfHarvest: 0.30, fallKrDefault: 600,  slaktKg: 50 },
    { key: 'vds_galt',    label: 'Galt',     pctOfHarvest: 0.28, fallKrDefault: 700,  slaktKg: 65 },
    { key: 'vds_arsgris', label: 'Årsgris',  pctOfHarvest: 0.42, fallKrDefault: 300,  slaktKg: 22 },
  ],
  mufflon: [
    { key: 'muf_kapital', label: 'Kapital Bagge',  pctOfHarvest: 0.20, fallKrDefault: 3500, slaktKg: 40 },
    { key: 'muf_bagge',   label: 'Mindre bagge',   pctOfHarvest: 0.25, fallKrDefault: 2000, slaktKg: 28 },
    { key: 'muf_hona',    label: 'Hona',           pctOfHarvest: 0.35, fallKrDefault: 1200, slaktKg: 22 },
    { key: 'muf_lamm',    label: 'Lamm',           pctOfHarvest: 0.20, fallKrDefault: 700,  slaktKg: 12 },
  ],
};

const COUNTIES = ['Blekinge','Dalarna','Gotland','Gävleborg','Halland','Jämtland','Jönköping','Kalmar','Kronoberg','Norrbotten','Skåne','Stockholm','Södermanland','Uppsala','Värmland','Västernorrland','Västmanland','Västra Götaland','Örebro','Östergötland'];

function clamp(n: number, lo: number, hi: number): number {
  return !Number.isFinite(n) ? lo : Math.min(hi, Math.max(lo, n));
}
function fmtKr(n: number): string { return n.toLocaleString('sv-SE') + ' kr'; }

function densityBand(ratio: number): { label: string; color: string } {
  if (!Number.isFinite(ratio) || ratio <= 0) return { label: 'Ingen / okänd', color: '#888' };
  if (ratio < 0.45) return { label: 'Lågt stamtryck', color: '#5c9ee8' };
  if (ratio < 0.85) return { label: 'Under referens', color: '#7cb87c' };
  if (ratio < 1.15) return { label: 'Rimligt', color: '#c9a227' };
  if (ratio < 1.65) return { label: 'Högt', color: '#e67e22' };
  return { label: 'Mycket högt', color: '#e74c3c' };
}

function SliderRow({ label, value, min, maxSlider, numMax, step, onChange, suffix, T, small }: {
  label: string; value: number; min: number; maxSlider: number; numMax: number;
  step: number; onChange: (n: number) => void; suffix?: string;
  T: ReturnType<typeof getTheme>; small?: boolean;
}) {
  const fs = small ? 12 : 13;
  return (
    <div style={{ marginBottom: small ? 10 : 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
        <span style={{ color: T.textSub, fontSize: fs, fontWeight: 600, flex: '1 1 120px', minWidth: 0 }}>{label}</span>
        <input type="number" min={min} max={numMax} step={step}
          value={Number.isFinite(value) ? value : min}
          onChange={e => { const v = parseFloat(e.target.value.replace(',','.')); if (!Number.isNaN(v)) onChange(clamp(Math.round(v/step)*step, min, numMax)); }}
          style={{ width: small ? 76 : 88, flexShrink: 0, boxSizing: 'border-box', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: small ? '4px 6px' : '6px 8px', color: T.text, fontSize: fs, fontWeight: 600, textAlign: 'right', fontFamily: 'inherit' }}
        />
        {suffix && <span style={{ color: T.textMuted, fontSize: fs - 1, flexShrink: 0 }}>{suffix}</span>}
      </div>
      <input type="range" min={min} max={Math.max(maxSlider, value, min+step)} step={step} value={clamp(value, min, Math.max(maxSlider, value))}
        onChange={e => onChange(clamp(Number(e.target.value), min, numMax))}
        style={{ width: '100%', accentColor: T.amber, display: 'block' }}
      />
    </div>
  );
}

function Toggle({ on, set, T }: { on: boolean; set: (v: boolean) => void; T: ReturnType<typeof getTheme> }) {
  return (
    <button type="button" onClick={() => set(!on)} style={{ width: 44, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer', background: on ? T.amber : T.border, position: 'relative', flexShrink: 0 }}>
      <span style={{ position: 'absolute', top: 3, left: on ? 22 : 4, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.35)' }} />
    </button>
  );
}

function Card({ T, title, children }: { T: ReturnType<typeof getTheme>; title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: '22px 22px', marginBottom: 20 }}>
      <div style={{ color: T.amber, fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 16 }}>{title}</div>
      {children}
    </div>
  );
}

function Chip({ active, T, onClick, children }: { active: boolean; T: ReturnType<typeof getTheme>; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} style={{ padding: '8px 16px', borderRadius: 20, border: `1.5px solid ${active ? T.amber : T.border}`, background: active ? T.amber : 'transparent', color: active ? '#000' : T.textSub, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
      {children}
    </button>
  );
}

const WIZARD_STEPS = [
  { label: 'Din mark' },
  { label: 'Viltstam' },
  { label: 'Jaktformer' },
  { label: 'Priser & kalkyl' },
  { label: 'Ansökan' },
];

function WizardStepper({ step, T, onStep }: { step: number; T: ReturnType<typeof getTheme>; onStep: (s: number) => void }) {
  return (
    <div style={{ padding: '20px clamp(16px,4vw,32px) 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, overflowX: 'auto', paddingBottom: 2 }}>
        {WIZARD_STEPS.map((s, i) => {
          const idx = i + 1;
          const done = step > idx;
          const active = step === idx;
          return (
            <React.Fragment key={idx}>
              <button
                type="button"
                onClick={() => { if (done) onStep(idx); }}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                  background: 'none', border: 'none', cursor: done ? 'pointer' : 'default',
                  padding: '0 6px', flexShrink: 0,
                }}
              >
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 800, flexShrink: 0,
                  background: done ? T.amber : active ? T.amber : T.surface,
                  border: `2px solid ${done || active ? T.amber : T.border}`,
                  color: done || active ? '#000' : T.textMuted,
                  transition: 'all 0.2s',
                }}>
                  {done ? '✓' : idx}
                </div>
                <span style={{ fontSize: 11, fontWeight: active ? 700 : 500, color: active ? T.amber : done ? T.text : T.textMuted, whiteSpace: 'nowrap' }}>
                  {s.label}
                </span>
              </button>
              {i < WIZARD_STEPS.length - 1 && (
                <div style={{ flex: 1, height: 2, background: step > idx ? T.amber : T.border, minWidth: 12, marginBottom: 24, transition: 'background 0.2s' }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

export default function ArrendeChat({ user, isAdmin, onMarkCreated }: ArrChatProps) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);

  const [wizardStep, setWizardStep] = useState(1);

  // Admin settings panel state
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminOk, setAdminOk] = useState('');
  const [adminErr, setAdminErr] = useState('');

  // Contract signing state
  const [contractStep, setContractStep] = useState<'form' | 'contract' | 'done'>('form');
  const [formMarkName, setFormMarkName] = useState('');
  const [formFastighets, setFormFastighets] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formMunicipality, setFormMunicipality] = useState('');
  const [formSignName, setFormSignName] = useState(user?.name ?? '');
  const [formAccepted, setFormAccepted] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formErr, setFormErr] = useState('');
  const [submittedContract, setSubmittedContract] = useState<import('./api').MarkOwnerContract | null>(null);

  // Mark
  const [areaHa, setAreaHa] = useState(250);
  const [county, setCounty] = useState('');

  // Viltstam
  const [counts, setCounts] = useState<Record<SpKey, number>>(() => Object.fromEntries(SPECIES.map(s => [s.key, 0])) as Record<SpKey, number>);
  const setCount = (k: SpKey, n: number) => setCounts(p => ({ ...p, [k]: n }));

  // Fällavgifter per subcat / species
  const [subcatFall, setSubcatFallState] = useState<Record<string, number>>(() => {
    const o: Record<string, number> = {};
    for (const subs of Object.values(SUBCATS)) for (const s of subs!) o[s.key] = s.fallKrDefault;
    return o;
  });
  const setSubcatFall = (k: string, n: number) => setSubcatFallState(p => ({ ...p, [k]: clamp(Math.round(n), 0, 500_000) }));

  const [speciesFall, setSpeciesFallState] = useState<Record<SpKey, number>>(() => Object.fromEntries(SPECIES.map(s => [s.key, s.fallKrDefault])) as Record<SpKey, number>);
  const setSpeciesFall = (k: SpKey, n: number) => setSpeciesFallState(p => ({ ...p, [k]: clamp(Math.round(n), 0, 500_000) }));

  // Kött kr/kg per species
  const [meatKgPris, setMeatKgPrisState] = useState<Record<SpKey, number>>(() => Object.fromEntries(SPECIES.map(s => [s.key, s.meatKgDefault])) as Record<SpKey, number>);
  const setMeatKgPris = (k: SpKey, n: number) => setMeatKgPrisState(p => ({ ...p, [k]: clamp(Math.round(n), 0, 2000) }));

  // Manuella uttag i viltvårdsplanen (null = auto)
  const [uttakOverride, setUttakOverride] = useState<Record<string, number | null>>({});
  const setUttak = (key: string, n: number | null) => setUttakOverride(p => ({ ...p, [key]: n }));

  // Jaktformer
  const [offerDrev, setOfferDrev] = useState(true);
  const [offerAlg, setOfferAlg] = useState(true);
  const [offerSmyg, setOfferSmyg] = useState(true);
  const [offerAtel, setOfferAtel] = useState(false);
  const [offerSkogsfagel, setOfferSkogsfagel] = useState(false);
  const [offerFasan, setOfferFasan] = useState(false);
  const [drevPerAr, setDrevPerAr] = useState(4);
  const [jagarePerDrev, setJagarePerDrev] = useState(10);
  const [smygdagar, setSmygdagar] = useState(12);
  const [atelkvallar, setAtelkvallar] = useState(8);

  // Priser per jaktform
  const [prisDrev, setPrisDrev] = useState(1100);
  const [prisAlg, setPrisAlg] = useState(4200);
  const [prisSmyg, setPrisSmyg] = useState(450);
  const [prisAtel, setPrisAtel] = useState(350);
  const [prisFagel, setPrisFagel] = useState(650);

  // Kött policy
  type MeatPolicy = 'jagare' | 'delvis' | 'mark';
  const [meatPolicy, setMeatPolicy] = useState<MeatPolicy>('delvis');

  // Utrustning
  type EqPolicy = 'mark' | 'delat' | 'jagare';
  const [equipment, setEquipment] = useState<EqPolicy>('delat');

  // Såldgrad
  const [saldOverride, setSaldOverride] = useState(false);
  const [saldManual, setSaldManual] = useState(82);

  // ── Load admin defaults from API on mount ─────────────────────────
  useEffect(() => {
    api.arrendeSettings().then(s => {
      if (!s) return;
      if (s.prisDrev != null) setPrisDrev(s.prisDrev);
      if (s.prisAlg != null) setPrisAlg(s.prisAlg);
      if (s.prisSmyg != null) setPrisSmyg(s.prisSmyg);
      if (s.prisAtel != null) setPrisAtel(s.prisAtel);
      if (s.prisFagel != null) setPrisFagel(s.prisFagel);
      try {
        const sf = JSON.parse(s.speciesFallJson || '{}');
        if (Object.keys(sf).length) setSpeciesFallState(prev => ({ ...prev, ...sf }));
      } catch { /**/ }
      try {
        const scf = JSON.parse(s.subcatFallJson || '{}');
        if (Object.keys(scf).length) setSubcatFallState(prev => ({ ...prev, ...scf }));
      } catch { /**/ }
      try {
        const mk = JSON.parse(s.meatKgJson || '{}');
        if (Object.keys(mk).length) setMeatKgPrisState(prev => ({ ...prev, ...mk }));
      } catch { /**/ }
    }).catch(() => {});
  }, []);

  const saveAdminDefaults = async () => {
    setAdminSaving(true); setAdminErr(''); setAdminOk('');
    try {
      await api.upsertArrendeSettings({
        speciesFallJson: JSON.stringify(speciesFall),
        subcatFallJson: JSON.stringify(subcatFall),
        meatKgJson: JSON.stringify(meatKgPris),
        prisDrev, prisAlg, prisSmyg, prisAtel, prisFagel,
      });
      setAdminOk('Standardvärden sparade! Dessa används nu som förslag för alla nya användare.');
      setTimeout(() => setAdminOk(''), 5000);
    } catch (e) {
      setAdminErr(e instanceof Error ? e.message : 'Något gick fel');
    } finally {
      setAdminSaving(false);
    }
  };

  // ── Economics ─────────────────────────────────────────────────────
  const econ = useMemo(() => {
    type HarvestRow = { spKey: SpKey; spLabel: string; stam: number; uttakTot: number; subcats: { key: string; label: string; antal: number; fallKr: number; slaktKg: number }[] };
    const harvest: HarvestRow[] = [];
    for (const sp of SPECIES) {
      const stam = counts[sp.key];
      if (stam <= 0) continue;
      const uttakAuto = Math.max(0, Math.round(stam * sp.harvFrac));
      const subs = SUBCATS[sp.key];
      if (subs) {
        const subcats = subs.map(sub => {
          const autoN = Math.round(uttakAuto * sub.pctOfHarvest);
          const overN = uttakOverride[sub.key];
          return { key: sub.key, label: sub.label, antal: overN != null ? overN : autoN, fallKr: subcatFall[sub.key] ?? sub.fallKrDefault, slaktKg: sub.slaktKg };
        });
        const uttakTot = subcats.reduce((a, s) => a + s.antal, 0);
        harvest.push({ spKey: sp.key, spLabel: sp.label, stam, uttakTot, subcats });
      } else {
        const uttakTot = uttakOverride[sp.key] != null ? uttakOverride[sp.key]! : uttakAuto;
        harvest.push({ spKey: sp.key, spLabel: sp.label, stam, uttakTot, subcats: [] });
      }
    }

    type Row = { id: string; beskr: string; antal: number; pris: number; summa: number };
    const rows: Row[] = [];

    if (offerDrev && drevPerAr > 0 && jagarePerDrev > 0) {
      const viltF = counts.radjur + counts.vildsvin + counts.dovhjort * 0.5;
      const dF = viltF >= 8 ? drevPerAr : Math.min(drevPerAr, Math.max(1, Math.floor(viltF / 8)));
      rows.push({ id:'drev', beskr:`Drevjakter (${dF} st × ${jagarePerDrev} jägare)`, antal:dF*jagarePerDrev, pris:prisDrev, summa:dF*jagarePerDrev*prisDrev });
    }

    for (const h of harvest) {
      if (h.subcats.length > 0) {
        const sum = h.subcats.reduce((a, s) => a + s.antal * s.fallKr, 0);
        if (sum > 0 && h.uttakTot > 0) rows.push({ id:`fall_${h.spKey}`, beskr:`Fällavgift ${h.spLabel}`, antal:h.uttakTot, pris:Math.round(sum/h.uttakTot), summa:sum });
      } else {
        const fk = speciesFall[h.spKey];
        if (fk > 0 && h.uttakTot > 0) rows.push({ id:`fall_${h.spKey}`, beskr:`Fällavgift ${h.spLabel}`, antal:h.uttakTot, pris:fk, summa:h.uttakTot*fk });
      }
    }

    if (offerAlg && counts.alg >= 1) {
      const pass = Math.min(3, Math.max(1, Math.ceil(counts.alg * 0.25)));
      rows.push({ id:'alg', beskr:`Älgjakt / passdag (${pass} pass)`, antal:pass, pris:prisAlg, summa:pass*prisAlg });
    }

    if (offerSmyg && smygdagar > 0) rows.push({ id:'smyg', beskr:`Smygjakt (${smygdagar} dagar × 4 jägare)`, antal:smygdagar*4, pris:prisSmyg, summa:smygdagar*4*prisSmyg });
    if (offerAtel && atelkvallar > 0) rows.push({ id:'atel', beskr:`Åteljakt (${atelkvallar} tillfällen × 3 jägare)`, antal:atelkvallar*3, pris:prisAtel, summa:atelkvallar*3*prisAtel });

    if (offerSkogsfagel && counts.skogsfagel >= 5) {
      const d = Math.min(6, Math.max(2, Math.floor(counts.skogsfagel / 15)));
      rows.push({ id:'skog', beskr:`Skogsfågel (${d} dagar × 5 jägare)`, antal:d*5, pris:prisFagel, summa:d*5*prisFagel });
    }

    if (offerFasan && counts.fasan >= 10) {
      const d = Math.min(8, Math.max(2, Math.floor(counts.fasan / 20)));
      rows.push({ id:'fas', beskr:`Fasan / and (${d} dagar × 6 jägare)`, antal:d*6, pris:prisFagel, summa:d*6*prisFagel });
    }

    if (meatPolicy !== 'jagare') {
      const frac = meatPolicy === 'mark' ? 1 : 0.5;
      for (const h of harvest) {
        const krKg = meatKgPris[h.spKey];
        if (krKg <= 0) continue;
        if (h.subcats.length > 0) {
          const sum = h.subcats.reduce((a, s) => a + s.antal * s.slaktKg * krKg * frac, 0);
          if (sum > 0) rows.push({ id:`kott_${h.spKey}`, beskr:`Köttintäkt ${h.spLabel} (${krKg} kr/kg)`, antal:h.uttakTot, pris:Math.round(sum/h.uttakTot), summa:Math.round(sum) });
        } else {
          const sp = SPECIES.find(s => s.key === h.spKey);
          if (!sp || h.uttakTot <= 0) continue;
          const avgKg = sp.key === 'fasan' ? 0.9 : sp.key === 'skogsfagel' ? 0.7 : 30;
          const sum = Math.round(h.uttakTot * avgKg * krKg * frac);
          if (sum > 0) rows.push({ id:`kott_${h.spKey}`, beskr:`Köttintäkt ${h.spLabel} (${krKg} kr/kg)`, antal:h.uttakTot, pris:Math.round(avgKg*krKg*frac), summa:sum });
        }
      }
    }

    if (equipment === 'mark') rows.push({ id:'utr', beskr:'Tillägg: utrustning (pass, torn, åtlar)', antal:1, pris:12000, summa:12000 });
    else if (equipment === 'delat') rows.push({ id:'utr2', beskr:'Tillägg: delat utrustningsansvar', antal:1, pris:4500, summa:4500 });

    const huntRaw = rows.filter(r => r.id!=='utr'&&r.id!=='utr2').reduce((a,r) => a+r.summa, 0);
    const equipRaw = rows.filter(r => r.id==='utr'||r.id==='utr2').reduce((a,r) => a+r.summa, 0);

    const saldAuto = clamp(0.95 - 0.02 * Math.log10(1 + huntRaw / 50000), 0.35, 0.97);
    const saldEff = saldOverride ? clamp(saldManual/100, 0, 1) : saldAuto;
    const brutto = Math.round(huntRaw * saldEff + equipRaw);
    const netto = Math.round(brutto * 0.8);

    return { harvest, rows, huntRaw, equipRaw, brutto, netto, saldAuto, saldEff };
  }, [counts, subcatFall, speciesFall, meatKgPris, uttakOverride, offerDrev, offerAlg, offerSmyg, offerAtel, offerSkogsfagel, offerFasan, drevPerAr, jagarePerDrev, smygdagar, atelkvallar, prisDrev, prisAlg, prisSmyg, prisAtel, prisFagel, meatPolicy, equipment, saldOverride, saldManual]);

  const tdR: React.CSSProperties = { padding: '8px 10px', textAlign: 'right' as const };
  const tdL: React.CSSProperties = { padding: '8px 10px', textAlign: 'left' as const };

  const navBtnBase: React.CSSProperties = { borderRadius: 8, padding: '13px 28px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', border: 'none' };

  function WizardNav({ onBack, onNext, nextLabel, nextDisabled }: { onBack?: () => void; onNext?: () => void; nextLabel?: string; nextDisabled?: boolean }) {
    return (
      <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', paddingTop: 8 }}>
        {onBack ? (
          <button type="button" onClick={onBack} style={{ ...navBtnBase, background: 'none', border: `1px solid ${T.border}`, color: T.textSub }}>← Tillbaka</button>
        ) : <div />}
        {onNext && (
          <button type="button" onClick={onNext} disabled={nextDisabled} style={{ ...navBtnBase, background: nextDisabled ? '#555' : T.amber, color: '#000', cursor: nextDisabled ? 'not-allowed' : 'pointer', opacity: nextDisabled ? 0.6 : 1 }}>
            {nextLabel ?? 'Nästa →'}
          </button>
        )}
      </div>
    );
  }

  // ── Admin panel ───────────────────────────────────────────────────
  if (isAdmin && adminOpen) {
    const iS: React.CSSProperties = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: '6px 10px', color: T.text, fontSize: 13, fontWeight: 600, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' };
    return (
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px clamp(12px,3vw,32px) 80px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <button onClick={() => setAdminOpen(false)} style={{ background: 'none', border: `1px solid ${T.border}`, color: T.textSub, borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>← Tillbaka</button>
          <div style={{ color: T.amber, fontSize: 16, fontWeight: 800 }}>Admin: Prisinställningar — Erbjud Jakt</div>
        </div>
        <p style={{ color: T.textSub, fontSize: 13, lineHeight: 1.7, marginBottom: 24 }}>
          Dessa värden används som standardförslag när en markägare öppnar kalkylatorn. Användaren kan alltid justera sina egna värden, men dessa är startvärdet.
        </p>

        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: '20px 22px', marginBottom: 16 }}>
          <div style={{ color: T.amber, fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 16 }}>Priser per jaktform (kr/jägare)</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px,1fr))', gap: 14 }}>
            {([['Drevjakt', prisDrev, setPrisDrev], ['Älgjakt / passdag', prisAlg, setPrisAlg], ['Smygjakt (per dag)', prisSmyg, setPrisSmyg], ['Åteljakt (per tillfälle)', prisAtel, setPrisAtel], ['Fågeljakt (per dag)', prisFagel, setPrisFagel]] as [string, number, (n: number) => void][]).map(([lbl, val, set]) => (
              <div key={lbl}>
                <label style={{ color: T.textSub, fontSize: 11, display: 'block', marginBottom: 4 }}>{lbl}</label>
                <input type="number" min={0} value={val} onChange={e => set(+e.target.value)} style={iS} />
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: '20px 22px', marginBottom: 16 }}>
          <div style={{ color: T.amber, fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 16 }}>Fällavgifter per underkategori (kr/djur)</div>
          {(Object.entries(SUBCATS) as [SpKey, SubCatDef[]][]).map(([spKey, subs]) => {
            const spLabel = SPECIES.find(s => s.key === spKey)?.label ?? spKey;
            return (
              <div key={spKey} style={{ marginBottom: 18 }}>
                <div style={{ color: T.textSub, fontSize: 12, fontWeight: 700, marginBottom: 8 }}>{spLabel}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px,1fr))', gap: 10 }}>
                  {subs.map(sub => (
                    <div key={sub.key}>
                      <label style={{ color: T.textSub, fontSize: 11, display: 'block', marginBottom: 3 }}>{sub.label}</label>
                      <input type="number" min={0} value={subcatFall[sub.key] ?? sub.fallKrDefault} onChange={e => setSubcatFall(sub.key, +e.target.value)} style={iS} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          <div style={{ marginTop: 8 }}>
            <div style={{ color: T.textSub, fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Arter utan underkategori (fasan, skogsfågel)</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px,1fr))', gap: 10 }}>
              {SPECIES.filter(s => !SUBCATS[s.key]).map(s => (
                <div key={s.key}>
                  <label style={{ color: T.textSub, fontSize: 11, display: 'block', marginBottom: 3 }}>{s.label}</label>
                  <input type="number" min={0} value={speciesFall[s.key] ?? s.fallKrDefault} onChange={e => setSpeciesFall(s.key, +e.target.value)} style={iS} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: '20px 22px', marginBottom: 24 }}>
          <div style={{ color: T.amber, fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 16 }}>Köttpriser (kr/kg)</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px,1fr))', gap: 14 }}>
            {SPECIES.filter(s => s.meatKgDefault > 0).map(s => (
              <div key={s.key}>
                <label style={{ color: T.textSub, fontSize: 11, display: 'block', marginBottom: 4 }}>{s.label}</label>
                <input type="number" min={0} value={meatKgPris[s.key] ?? s.meatKgDefault} onChange={e => setMeatKgPris(s.key, +e.target.value)} style={iS} />
              </div>
            ))}
          </div>
        </div>

        {adminErr && <div style={{ background: 'rgba(192,57,43,0.12)', border: '1px solid rgba(192,57,43,0.3)', borderRadius: 6, padding: '10px 14px', color: '#ff6b6b', fontSize: 13, marginBottom: 12 }}>{adminErr}</div>}
        {adminOk && <div style={{ background: 'rgba(46,125,50,0.12)', border: '1px solid rgba(76,175,80,0.3)', borderRadius: 6, padding: '10px 14px', color: '#4caf50', fontSize: 13, marginBottom: 12 }}>{adminOk}</div>}
        <button disabled={adminSaving} onClick={saveAdminDefaults} style={{ background: T.amber, color: '#000', border: 'none', borderRadius: 8, padding: '13px 32px', fontSize: 15, fontWeight: 800, cursor: adminSaving ? 'not-allowed' : 'pointer', opacity: adminSaving ? 0.7 : 1, fontFamily: 'inherit' }}>
          {adminSaving ? 'Sparar...' : 'Spara standardvärden'}
        </button>
      </div>
    );
  }

  // ── Wizard ────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: 1020, margin: '0 auto', padding: '0 0 80px' }}>

      {isAdmin && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px clamp(16px,4vw,32px) 0' }}>
          <button onClick={() => setAdminOpen(true)} style={{ background: 'rgba(238,143,9,0.1)', border: `1px solid rgba(238,143,9,0.4)`, color: 'rgba(238,143,9,0.9)', borderRadius: 6, padding: '6px 16px', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' }}>
            ⚙ Admin: Prisinställningar
          </button>
        </div>
      )}

      <WizardStepper step={wizardStep} T={T} onStep={s => setWizardStep(s)} />

      {/* Sticky revenue bar — visible from step 2 onwards */}
      {wizardStep >= 2 && (
        <div style={{ position: 'sticky', top: 0, zIndex: 200, background: `${T.surface}f5`, backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderTop: `2px solid ${T.amber}`, borderBottom: `1px solid ${T.border}`, padding: '10px clamp(16px,4vw,32px)', display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', flexShrink: 0 }}>Intäkter (live)</div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', flex: 1 }}>
            <div>
              <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 600, marginBottom: 1 }}>Brutto / år</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: T.amber, lineHeight: 1 }}>{fmtKr(econ.brutto)}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 600, marginBottom: 1 }}>Netto (efter 20%)</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: T.success, lineHeight: 1 }}>{fmtKr(econ.netto)}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 600, marginBottom: 1 }}>Såldgrad</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{(econ.saldEff*100).toFixed(0)}%</div>
            </div>
          </div>
          <div style={{ fontSize: 10, color: T.textMuted, maxWidth: 160, lineHeight: 1.4 }}>Illustration — avtal och lokala förutsättningar styr.</div>
        </div>
      )}

      <div style={{ padding: '24px clamp(16px,4vw,32px) 0' }}>

        {/* ── Step 1: Din mark ── */}
        {wizardStep === 1 && (
          <>
            {/* Hero */}
            <div style={{ background: `linear-gradient(160deg, ${T.forest}cc 0%, ${T.bg} 100%)`, borderRadius: 12, border: `1px solid ${T.border}`, padding: '40px clamp(20px,4vw,48px) 36px', marginBottom: 24 }}>
              <div style={{ maxWidth: 640 }}>
                <div style={{ display: 'inline-block', background: T.amber, color: '#000', fontSize: 10, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', padding: '4px 12px', borderRadius: 20, marginBottom: 14 }}>Qvenari för markägare</div>
                <h1 style={{ color: T.text, fontSize: 'clamp(22px,4vw,36px)', fontWeight: 900, lineHeight: 1.15, margin: '0 0 14px', letterSpacing: '-0.5px' }}>
                  Behåll kontrollen.<br/><span style={{ color: T.amber }}>Tjäna mer på jakten.</span>
                </h1>
                <p style={{ color: T.textSub, fontSize: 14, lineHeight: 1.7, margin: '0 0 20px', maxWidth: 560 }}>
                  Traditionellt arrende ger fasta intäkter men du förlorar kontrollen. Med Qvenari paketerar du jakten själv: selekterade jägare, digitalt viltförvaltningsunderlag och intäkter per jaktdag, fällt djur och kött.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, maxWidth: 620 }}>
                  {[
                    ['Selekterade jägare', 'Du godkänner varje grupp.'],
                    ['Viltförvaltning', 'Avskjutningsplan och statistik i realtid.'],
                    ['Fler intäktsben', 'Fällavgifter, jaktdagar, kött.'],
                    ['Digital plattform', 'Bokningar, betalningar och kommunikation.'],
                  ].map(([title, desc]) => (
                    <div key={title} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: '12px 14px' }}>
                      <div style={{ color: T.text, fontSize: 12, fontWeight: 700, marginBottom: 3 }}>{title}</div>
                      <div style={{ color: T.textSub, fontSize: 11, lineHeight: 1.5 }}>{desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Comparison table */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ color: T.textMuted, fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 12 }}>Qvenari vs Traditionellt arrende</div>
              <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${T.border}` }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: T.surface }}>
                      <th style={{ ...tdL, borderBottom: `1px solid ${T.border}`, color: T.textMuted, fontWeight: 700 }}>Aspekt</th>
                      <th style={{ ...tdR, borderBottom: `1px solid ${T.border}`, color: T.amber, fontWeight: 800 }}>Qvenari</th>
                      <th style={{ ...tdR, borderBottom: `1px solid ${T.border}`, color: T.textSub, fontWeight: 700 }}>Traditionellt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Kontroll över vem som jagar', 'Du väljer', 'Jaktlaget bestämmer'],
                      ['Intäktsmodell', 'Fällavgift + jaktdagar + kött', 'Fast arrendeavgift'],
                      ['Viltförvaltningsdata', 'Digital, realtid', 'Informellt / manuellt'],
                      ['Flexibilitet per säsong', 'Paketeras om vid behov', 'Bundet kontrakt'],
                      ['Insyn i avskjutning', 'App-logg per kategori', 'Ofta begränsad'],
                      ['Jägarnätverk', 'Verifierade profiler', 'Begränsat till kända'],
                    ].map(([asp, q, t]) => (
                      <tr key={asp} style={{ borderBottom: `1px solid ${T.border}` }}>
                        <td style={{ ...tdL, color: T.text, fontWeight: 600 }}>{asp}</td>
                        <td style={{ ...tdR, color: T.text }}>{q}</td>
                        <td style={{ ...tdR, color: T.textSub }}>{t}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Din mark form */}
            <Card T={T} title="Din mark">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px,1fr))', gap: 14, marginBottom: 18 }}>
                <div>
                  <label style={{ display: 'block', color: T.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>Län</label>
                  <select value={county} onChange={e => setCounty(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, padding: '10px 12px', color: T.text, fontSize: 14, fontFamily: 'inherit' }}>
                    <option value="">Välj län…</option>
                    {COUNTIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <SliderRow label="Markens storlek (hektar)" value={areaHa} min={1} maxSlider={15000} numMax={500000} step={10} onChange={setAreaHa} suffix="ha" T={T} />
            </Card>

            <WizardNav onNext={() => setWizardStep(2)} nextLabel="Nästa: Viltstam →" />
          </>
        )}

        {/* ── Step 2: Viltstam ── */}
        {wizardStep === 2 && (
          <>
            <Card T={T} title="Viltstam och täthet">
              <p style={{ color: T.textSub, fontSize: 12, margin: '0 0 18px', lineHeight: 1.6 }}>Ange ungefärlig stam per art. Täthetsmarkeringen jämför med vägledande riksnivå.</p>
              {SPECIES.map(s => {
                const ref = (areaHa / 100) * s.refPer100Ha;
                const cnt = counts[s.key];
                const band = densityBand(ref > 0.01 ? cnt / ref : 0);
                return (
                  <div key={s.key} style={{ marginBottom: 20, paddingBottom: 18, borderBottom: `1px solid ${T.border}` }}>
                    <SliderRow label={s.label} value={cnt} min={0} maxSlider={Math.max(500, Math.ceil(ref*12))} numMax={500_000} step={1} onChange={n => setCount(s.key, n)} suffix={`indiv. · ref ~${Math.round(ref)}`} T={T} />
                    {cnt > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                        <div style={{ flex: 1, height: 5, borderRadius: 3, background: 'linear-gradient(90deg, #2a4a6e 0%, #3d6b4a 35%, #c9a227 55%, #c45c2a 100%)', opacity: 0.7, position: 'relative' }}>
                          <div style={{ position: 'absolute', top: -3, left: `${Math.min(98, Math.max(2, (Math.log10(0.05 + cnt/Math.max(ref,0.01)) / Math.log10(2.8)) * 100))}%`, width: 11, height: 11, borderRadius: '50%', background: T.text, border: `2px solid ${T.amber}`, transform: 'translateX(-50%)' }} />
                        </div>
                        <span style={{ fontSize: 10, color: band.color, fontWeight: 700, whiteSpace: 'nowrap' }}>{band.label}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </Card>

            <WizardNav onBack={() => setWizardStep(1)} onNext={() => setWizardStep(3)} nextLabel="Nästa: Jaktformer →" />
          </>
        )}

        {/* ── Step 3: Jaktformer & policy ── */}
        {wizardStep === 3 && (
          <>
            <Card T={T} title="Jaktformer">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {([
                  { on: offerDrev, set: setOfferDrev, label: 'Drevjakt (klövvilt, räv m.m.)' },
                  { on: offerAlg,  set: setOfferAlg,  label: 'Älgjakt / passdag' },
                  { on: offerSmyg, set: setOfferSmyg, label: 'Smygjakt / stilljakt' },
                  { on: offerAtel, set: setOfferAtel, label: 'Åteljakt' },
                  { on: offerSkogsfagel, set: setOfferSkogsfagel, label: 'Skogsfågel (tjäder, orre)' },
                  { on: offerFasan,      set: setOfferFasan,      label: 'Fasan, rapphöns, andjakt' },
                ] as const).map(row => (
                  <div key={row.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ color: T.text, fontSize: 14 }}>{row.label}</span>
                    <Toggle on={row.on} set={row.set} T={T} />
                  </div>
                ))}
                {offerDrev && (
                  <div style={{ padding: '14px 16px', background: T.bg, borderRadius: 8, border: `1px solid ${T.border}` }}>
                    <SliderRow label="Drevjakter per år" value={drevPerAr} min={0} maxSlider={40} numMax={120} step={1} onChange={setDrevPerAr} T={T} small />
                    <SliderRow label="Jägare per drev" value={jagarePerDrev} min={0} maxSlider={50} numMax={100} step={1} onChange={setJagarePerDrev} T={T} small />
                  </div>
                )}
                {(offerSmyg || offerAtel) && (
                  <div style={{ padding: '14px 16px', background: T.bg, borderRadius: 8, border: `1px solid ${T.border}` }}>
                    {offerSmyg && <SliderRow label="Smygjakt: jaktdagar per år" value={smygdagar} min={0} maxSlider={100} numMax={300} step={1} onChange={setSmygdagar} T={T} small />}
                    {offerAtel  && <SliderRow label="Åteljakt: tillfällen per år" value={atelkvallar} min={0} maxSlider={60} numMax={200} step={1} onChange={setAtelkvallar} T={T} small />}
                  </div>
                )}
              </div>
            </Card>

            <Card T={T} title="Kött och utrustning">
              <div style={{ marginBottom: 20 }}>
                <div style={{ color: T.textSub, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>Kött — vem behåller?</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {([['jagare','Jägarna behåller köttet'],['delvis','Markägaren tar del av köttet'],['mark','Markägaren behåller allt kött']] as const).map(([k, lab]) => (
                    <Chip key={k} active={meatPolicy===k} T={T} onClick={() => setMeatPolicy(k)}>{lab}</Chip>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ color: T.textSub, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>Utrustning på marken</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {([['mark','Pass, torn, åtlar finns'],['delat','Delat ansvar'],['jagare','Jägarna ordnar eget']] as const).map(([k, lab]) => (
                    <Chip key={k} active={equipment===k} T={T} onClick={() => setEquipment(k)}>{lab}</Chip>
                  ))}
                </div>
              </div>
            </Card>

            <WizardNav onBack={() => setWizardStep(2)} onNext={() => setWizardStep(4)} nextLabel="Nästa: Priser & kalkyl →" />
          </>
        )}

        {/* ── Step 4: Priser & kalkyl ── */}
        {wizardStep === 4 && (
          <>
            <Card T={T} title="Fällavgifter (kr per djur)">
              <p style={{ color: T.textSub, fontSize: 12, margin: '0 0 18px', lineHeight: 1.6 }}>Sätt avgift per kategori — underkategorierna prissätts separat.</p>
              {SPECIES.map(s => {
                const subs = SUBCATS[s.key];
                if (s.key === 'skogsfagel') return null;
                if (subs) return (
                  <div key={s.key} style={{ marginBottom: 22, paddingBottom: 18, borderBottom: `1px solid ${T.border}` }}>
                    <div style={{ color: T.text, fontSize: 13, fontWeight: 800, marginBottom: 12 }}>{s.label}</div>
                    {subs.map(sub => (
                      <SliderRow key={sub.key} label={sub.label} value={subcatFall[sub.key] ?? sub.fallKrDefault} min={0} maxSlider={Math.max(12000, sub.fallKrDefault*4)} numMax={500_000} step={50} onChange={n => setSubcatFall(sub.key, n)} suffix="kr" T={T} small />
                    ))}
                  </div>
                );
                return (
                  <div key={s.key} style={{ marginBottom: 14 }}>
                    <SliderRow label={s.label} value={speciesFall[s.key]} min={0} maxSlider={Math.max(10000, s.fallKrDefault*4)} numMax={500_000} step={50} onChange={n => setSpeciesFall(s.key, n)} suffix="kr" T={T} />
                  </div>
                );
              })}
            </Card>

            {meatPolicy !== 'jagare' && (
              <Card T={T} title="Köttintäkt (kr per kg slaktvikt)">
                <p style={{ color: T.textSub, fontSize: 12, margin: '0 0 16px', lineHeight: 1.6 }}>
                  Jägaren eller annan köpare betalar kr/kg för slaktat vilt. Vid "delvis" räknas 50% av vikten till mark.
                </p>
                <div style={{ overflowX: 'auto', marginBottom: 18 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ color: T.textMuted }}>
                        <th style={{ ...tdL, borderBottom: `1px solid ${T.border}`, fontWeight: 700 }}>Kategori</th>
                        <th style={{ ...tdR, borderBottom: `1px solid ${T.border}`, fontWeight: 700 }}>Slaktvikt</th>
                        <th style={{ ...tdR, borderBottom: `1px solid ${T.border}`, fontWeight: 700 }}>Kr/kg</th>
                        <th style={{ ...tdR, borderBottom: `1px solid ${T.border}`, fontWeight: 700 }}>Pris/djur</th>
                      </tr>
                    </thead>
                    <tbody>
                      {SPECIES.filter(s => s.meatKgDefault > 0).map(sp => {
                        const subs = SUBCATS[sp.key];
                        const krKg = meatKgPris[sp.key];
                        const frac = meatPolicy === 'mark' ? 1 : 0.5;
                        if (subs) return (
                          <React.Fragment key={sp.key}>
                            <tr style={{ background: T.bg }}>
                              <td style={{ ...tdL, fontWeight: 800, color: T.text, borderTop: `1px solid ${T.border}`, paddingTop: 10 }} colSpan={4}>{sp.label}</td>
                            </tr>
                            {subs.map(sub => (
                              <tr key={sub.key}>
                                <td style={{ ...tdL, paddingLeft: 20, color: T.textSub }}>— {sub.label}</td>
                                <td style={{ ...tdR, color: T.textMuted }}>{sub.slaktKg} kg</td>
                                <td style={{ ...tdR, color: T.text, fontWeight: 700 }}>{krKg} kr</td>
                                <td style={{ ...tdR, color: T.amber }}>{fmtKr(Math.round(sub.slaktKg * krKg * frac))}</td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                        return null;
                      })}
                    </tbody>
                  </table>
                </div>
                {SPECIES.filter(s => s.meatKgDefault > 0).map(s => (
                  <SliderRow key={s.key} label={s.label + ' — kr/kg'} value={meatKgPris[s.key]} min={0} maxSlider={250} numMax={2000} step={5} onChange={n => setMeatKgPris(s.key, n)} suffix="kr/kg" T={T} small />
                ))}
              </Card>
            )}

            <Card T={T} title="Jaktpaketpriser">
              <SliderRow label="Pris per jägare och drev" value={prisDrev} min={0} maxSlider={6000} numMax={100_000} step={50} onChange={setPrisDrev} suffix="kr" T={T} />
              <SliderRow label="Älgpass / dag (snitt)" value={prisAlg} min={0} maxSlider={15000} numMax={200_000} step={100} onChange={setPrisAlg} suffix="kr" T={T} />
              <SliderRow label="Smygjakt per jägare och dag" value={prisSmyg} min={0} maxSlider={4000} numMax={50_000} step={25} onChange={setPrisSmyg} suffix="kr" T={T} />
              <SliderRow label="Åteljakt per jägare och tillfälle" value={prisAtel} min={0} maxSlider={3000} numMax={40_000} step={25} onChange={setPrisAtel} suffix="kr" T={T} />
              <SliderRow label="Fågeljakt per jägare och dag" value={prisFagel} min={0} maxSlider={5000} numMax={60_000} step={25} onChange={setPrisFagel} suffix="kr" T={T} />
            </Card>

            {/* Viltförvaltningstabell */}
            <Card T={T} title="Viltförvaltning — planerat uttag">
              <p style={{ color: T.textSub, fontSize: 12, margin: '0 0 16px', lineHeight: 1.6 }}>
                Illustrativt årsuttag per art och kategori. Antal beräknas automatiskt men kan justeras manuellt. Används som underlag i Qvenari — ej juridiskt avskjutningsbeslut.
              </p>
              {econ.harvest.length === 0 ? (
                <div style={{ color: T.textMuted, fontSize: 13, padding: '20px 0' }}>Öka antal individer i viltstam-steget för att se förslag.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ color: T.textMuted }}>
                        <th style={{ ...tdL, borderBottom: `2px solid ${T.border}` }}>Art / Kategori</th>
                        <th style={{ ...tdR, borderBottom: `2px solid ${T.border}` }}>Stam</th>
                        <th style={{ ...tdR, borderBottom: `2px solid ${T.border}` }}>Uttag</th>
                        <th style={{ ...tdR, borderBottom: `2px solid ${T.border}` }}>Slaktvikt</th>
                        <th style={{ ...tdR, borderBottom: `2px solid ${T.border}` }}>Fällavgift</th>
                      </tr>
                    </thead>
                    <tbody>
                      {econ.harvest.map(h => (
                        <React.Fragment key={h.spKey}>
                          <tr style={{ background: T.bg }}>
                            <td style={{ ...tdL, fontWeight: 800, color: T.text, borderTop: `1px solid ${T.border}` }}>{h.spLabel}</td>
                            <td style={{ ...tdR, color: T.textSub, borderTop: `1px solid ${T.border}` }}>{h.stam}</td>
                            <td style={{ ...tdR, color: T.amber, fontWeight: 700, borderTop: `1px solid ${T.border}` }}>{h.uttakTot} tot</td>
                            <td style={{ ...tdR, color: T.textMuted, fontSize: 11, borderTop: `1px solid ${T.border}` }}>—</td>
                            <td style={{ ...tdR, color: T.textMuted, fontSize: 11, borderTop: `1px solid ${T.border}` }}>vägd snitt</td>
                          </tr>
                          {h.subcats.map(sub => {
                            const isOverridden = uttakOverride[sub.key] != null;
                            return (
                              <tr key={sub.key}>
                                <td style={{ ...tdL, color: T.textSub, fontSize: 12, paddingLeft: 22 }}>— {sub.label}</td>
                                <td style={{ ...tdR, color: T.textMuted, fontSize: 11 }}>—</td>
                                <td style={{ ...tdR, fontSize: 12 }}>
                                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                    <input type="number" min={0} max={9999} value={sub.antal}
                                      onChange={e => { const v = parseInt(e.target.value); setUttak(sub.key, isNaN(v) ? null : Math.max(0, v)); }}
                                      style={{ width: 52, background: isOverridden ? `${T.amber}22` : T.surface, border: `1px solid ${isOverridden ? T.amber : T.border}`, borderRadius: 4, padding: '2px 4px', color: T.text, fontSize: 12, textAlign: 'right', fontFamily: 'inherit' }}
                                    />
                                    {isOverridden && (
                                      <button type="button" onClick={() => setUttak(sub.key, null)} title="Återställ auto" style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 11, padding: '0 2px', fontFamily: 'inherit' }}>↺</button>
                                    )}
                                  </div>
                                </td>
                                <td style={{ ...tdR, color: T.textMuted, fontSize: 11 }}>{sub.slaktKg} kg</td>
                                <td style={{ ...tdR, color: T.textSub, fontSize: 12 }}>{sub.antal > 0 ? fmtKr(sub.fallKr) : '—'}</td>
                              </tr>
                            );
                          })}
                          {h.subcats.length === 0 && (
                            <tr>
                              <td style={{ ...tdL, paddingLeft: 22, color: T.textSub, fontSize: 12 }}>— (totalt uttag)</td>
                              <td style={{ ...tdR, color: T.textMuted, fontSize: 11 }}>—</td>
                              <td style={{ ...tdR, fontSize: 12 }}>
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  <input type="number" min={0} max={9999} value={h.uttakTot}
                                    onChange={e => { const v = parseInt(e.target.value); setUttak(h.spKey, isNaN(v) ? null : Math.max(0, v)); }}
                                    style={{ width: 52, background: uttakOverride[h.spKey] != null ? `${T.amber}22` : T.surface, border: `1px solid ${uttakOverride[h.spKey] != null ? T.amber : T.border}`, borderRadius: 4, padding: '2px 4px', color: T.text, fontSize: 12, textAlign: 'right', fontFamily: 'inherit' }}
                                  />
                                  {uttakOverride[h.spKey] != null && (
                                    <button type="button" onClick={() => setUttak(h.spKey, null)} title="Återställ auto" style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 11, padding: '0 2px', fontFamily: 'inherit' }}>↺</button>
                                  )}
                                </div>
                              </td>
                              <td style={{ ...tdR, color: T.textMuted, fontSize: 11 }}>—</td>
                              <td style={{ ...tdR, color: T.textSub, fontSize: 12 }}>{h.uttakTot > 0 ? fmtKr(speciesFall[h.spKey]) : '—'}</td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* Såldgrad och intäktssammanställning */}
            <Card T={T} title="Intäktssammanställning">
              <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: 16, marginBottom: 20 }}>
                <div style={{ color: T.textSub, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>Såldgrad</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: saldOverride ? 12 : 0 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: T.text, fontSize: 13 }}>
                    <input type="checkbox" checked={saldOverride} onChange={e => setSaldOverride(e.target.checked)} style={{ accentColor: T.amber, width: 16, height: 16 }} />
                    Manuell såldgrad
                  </label>
                  <span style={{ color: T.amber, fontSize: 14, fontWeight: 700 }}>Auto: {(econ.saldAuto*100).toFixed(1)}%</span>
                </div>
                {saldOverride && <SliderRow label="Såldgrad" value={saldManual} min={0} maxSlider={100} numMax={100} step={1} onChange={setSaldManual} suffix="%" T={T} />}
                <div style={{ fontSize: 12, color: T.textMuted, marginTop: 8 }}>Tillämpas: <strong style={{ color: T.text }}>{(econ.saldEff*100).toFixed(1)}%</strong> {saldOverride ? '(manuell)' : '(auto)'}</div>
              </div>

              {econ.rows.length === 0 ? (
                <div style={{ color: T.textMuted, fontSize: 13, padding: '12px 0' }}>Aktivera jaktformer och lägg in stam i föregående steg för att se intäktsrader.</div>
              ) : (
                <div style={{ borderRadius: 8, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ color: T.textMuted, background: T.surface }}>
                        <th style={{ ...tdL, borderBottom: `1px solid ${T.border}` }}>Post</th>
                        <th style={{ ...tdR, borderBottom: `1px solid ${T.border}` }}>Antal</th>
                        <th style={{ ...tdR, borderBottom: `1px solid ${T.border}` }}>Snittpris</th>
                        <th style={{ ...tdR, borderBottom: `1px solid ${T.border}` }}>Summa</th>
                      </tr>
                    </thead>
                    <tbody>
                      {econ.rows.map(r => (
                        <tr key={r.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                          <td style={{ ...tdL, color: T.text }}>{r.beskr}</td>
                          <td style={{ ...tdR, color: T.textSub }}>{r.antal}</td>
                          <td style={{ ...tdR, color: T.textSub }}>{r.pris.toLocaleString('sv-SE')} kr</td>
                          <td style={{ ...tdR, color: T.amber, fontWeight: 700 }}>{r.summa.toLocaleString('sv-SE')} kr</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ padding: '20px 18px', background: `${T.amber}18`, borderTop: `1px solid ${T.border}` }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px,1fr))', gap: 16 }}>
                      <div>
                        <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, marginBottom: 4 }}>Råsumma (jakt + fäll + kött)</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{fmtKr(econ.huntRaw)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, marginBottom: 4 }}>Utrustning (skalas ej)</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{fmtKr(econ.equipRaw)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, marginBottom: 4 }}>Brutto / år ({(econ.saldEff*100).toFixed(0)}%)</div>
                        <div style={{ fontSize: 28, fontWeight: 900, color: T.amber }}>{fmtKr(econ.brutto)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, marginBottom: 4 }}>Netto (efter 20% plattform)</div>
                        <div style={{ fontSize: 28, fontWeight: 900, color: T.success }}>{fmtKr(econ.netto)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Card>

            <WizardNav onBack={() => setWizardStep(3)} onNext={() => setWizardStep(5)} nextLabel="Nästa: Ansökan →" />
          </>
        )}

        {/* ── Step 5: Ansökan ── */}
        {wizardStep === 5 && (
          <>
            {user ? (
              submittedContract || contractStep === 'done' ? (
                <Card T={T} title="Ansökan mottagen!">
                  <div style={{ background: 'rgba(46,125,50,0.12)', border: '1px solid rgba(76,175,80,0.3)', borderRadius: 8, padding: '20px 22px', marginBottom: 20 }}>
                    <div style={{ fontSize: 20, marginBottom: 8 }}>✅</div>
                    <div style={{ color: T.text, fontSize: 15, fontWeight: 700, marginBottom: 8 }}>Avtalet är signerat och ansökan är skickad!</div>
                    <p style={{ color: T.textSub, fontSize: 13, lineHeight: 1.7, margin: 0 }}>
                      Din mark <strong>{submittedContract?.markName}</strong> är skapad med status <em>Opublicerad</em>. En Qvenari-ansvarig granskar din ansökan och skriver under avtalet från Qvenaris sida. Marken publiceras automatiskt och du meddelas via e-post.
                    </p>
                  </div>
                  <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: '16px 18px', fontSize: 13 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div><span style={{ color: T.textMuted }}>Mark:</span> <strong style={{ color: T.text }}>{submittedContract?.markName}</strong></div>
                      <div><span style={{ color: T.textMuted }}>Areal:</span> <strong style={{ color: T.text }}>{submittedContract?.areaHa} ha</strong></div>
                      <div><span style={{ color: T.textMuted }}>Status:</span> <strong style={{ color: T.amber }}>Väntar på admin-signering</strong></div>
                      <div><span style={{ color: T.textMuted }}>E-post:</span> <strong style={{ color: T.text }}>{submittedContract?.registrantEmail}</strong></div>
                    </div>
                  </div>
                  <div style={{ marginTop: 16, fontSize: 11, color: T.textMuted, lineHeight: 1.6 }}>
                    Frågor? Kontakta oss på <a href="mailto:mark@qvenari.se" style={{ color: T.amber }}>mark@qvenari.se</a>
                  </div>
                </Card>
              ) : contractStep === 'form' ? (
                <Card T={T} title="Registrera din mark digitalt">
                  <p style={{ color: T.textSub, fontSize: 14, lineHeight: 1.7, margin: '0 0 20px' }}>
                    Fyll i grunduppgifter om din mark. Kalkylatorn sparas automatiskt som ditt avskjutningsunderlag. Du signerar sedan ett avtal digitalt — marken skapas direkt men publiceras först när Qvenari också skrivit under.
                  </p>
                  <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 18 }}>Inloggad som <strong style={{ color: T.text }}>{user.email}</strong></div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))', gap: 14, marginBottom: 18 }}>
                    <div>
                      <label style={{ display: 'block', color: T.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Markens namn *</label>
                      <input value={formMarkName} onChange={e => setFormMarkName(e.target.value)} placeholder="t.ex. Brandsbol södra" style={{ width: '100%', boxSizing: 'border-box', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, padding: '10px 12px', color: T.text, fontSize: 14, fontFamily: 'inherit' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: T.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Fastighetsbeteckning</label>
                      <input value={formFastighets} onChange={e => setFormFastighets(e.target.value)} placeholder="t.ex. Gräsmark 1:14" style={{ width: '100%', boxSizing: 'border-box', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, padding: '10px 12px', color: T.text, fontSize: 14, fontFamily: 'inherit' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: T.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Telefon</label>
                      <input value={formPhone} onChange={e => setFormPhone(e.target.value)} placeholder="+46 70 000 00 00" style={{ width: '100%', boxSizing: 'border-box', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, padding: '10px 12px', color: T.text, fontSize: 14, fontFamily: 'inherit' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', color: T.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Kommun</label>
                      <input value={formMunicipality} onChange={e => setFormMunicipality(e.target.value)} placeholder="t.ex. Filipstad" style={{ width: '100%', boxSizing: 'border-box', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, padding: '10px 12px', color: T.text, fontSize: 14, fontFamily: 'inherit' }} />
                    </div>
                  </div>

                  <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: '14px 16px', marginBottom: 18, fontSize: 12, color: T.textSub, lineHeight: 1.6 }}>
                    <strong style={{ color: T.text }}>Din avskjutningsplan sparas automatiskt</strong> — alla viltstammar, uttag och priser du angett ingår i ansökan.
                    {econ.harvest.length > 0 && (
                      <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                        {econ.harvest.map(h => (
                          <li key={h.spKey}>{h.spLabel}: stam {h.stam}, uttag {h.uttakTot} djur/år</li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {formErr && <div style={{ background: 'rgba(192,57,43,0.12)', border: '1px solid rgba(192,57,43,0.3)', borderRadius: 6, padding: '10px 14px', color: '#ff6b6b', fontSize: 13, marginBottom: 14 }}>{formErr}</div>}

                  <WizardNav
                    onBack={() => setWizardStep(4)}
                    onNext={() => {
                      if (!formMarkName.trim()) { setFormErr('Fyll i markens namn'); return; }
                      if (areaHa <= 0) { setFormErr('Ange markens areal i hektar (se "Din mark" i steg 1)'); return; }
                      setFormErr('');
                      setContractStep('contract');
                    }}
                    nextLabel="Gå vidare till avtal →"
                  />
                </Card>
              ) : (
                <Card T={T} title="Avtal — Erbjud jakt via Qvenari">
                  <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: '20px 22px', marginBottom: 20, fontSize: 13, lineHeight: 1.8, color: T.textSub }}>
                    <div style={{ color: T.amber, fontWeight: 800, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 14 }}>Uppdragsavtal — Qvenari Markägarplattform</div>
                    <p style={{ margin: '0 0 8px' }}><strong style={{ color: T.text }}>Markägare:</strong> {user.email}{user.name ? ` (${user.name})` : ''}</p>
                    <p style={{ margin: '0 0 8px' }}><strong style={{ color: T.text }}>Mark:</strong> {formMarkName}{formFastighets ? ` (${formFastighets})` : ''}, {areaHa} ha{county ? `, ${county}` : ''}</p>
                    <p style={{ margin: '0 0 8px' }}><strong style={{ color: T.text }}>Uppskattad bruttointäkt:</strong> {econ.brutto.toLocaleString('sv-SE')} kr/år</p>

                    <div style={{ borderTop: `1px solid ${T.border}`, margin: '16px 0', paddingTop: 16 }}>
                      <strong style={{ color: T.text, display: 'block', marginBottom: 10 }}>Villkor</strong>
                      <ol style={{ margin: 0, paddingLeft: 20 }}>
                        <li>Markägaren ger Qvenari AB rätt att marknadsföra och förmedla jakt på ovan angiven mark via plattformen qvenari.se.</li>
                        <li>Qvenari tar en plattformsavgift om 20 % på förmedlade intäkter (jaktdagar, fällavgifter och köttersättning).</li>
                        <li>Markägaren ansvarar för att ha laglig jakträtt och nödvändiga tillstånd för marken.</li>
                        <li>Avskjutningsplanen i denna ansökan är vägledande och justeras i samråd inför varje säsong.</li>
                        <li>Avtalet gäller löpande tills det sägs upp av endera part med 30 dagars varsel.</li>
                        <li>Qvenari förbehåller sig rätten att granska och godkänna marken innan publicering.</li>
                        <li>Marken publiceras på plattformen efter att Qvenaris representant undertecknat detta avtal.</li>
                      </ol>
                    </div>

                    {econ.harvest.length > 0 && (
                      <div style={{ borderTop: `1px solid ${T.border}`, margin: '16px 0', paddingTop: 16 }}>
                        <strong style={{ color: T.text, display: 'block', marginBottom: 10 }}>Avskjutningsplan (vägledande)</strong>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ color: T.textMuted }}>
                              <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: `1px solid ${T.border}` }}>Art</th>
                              <th style={{ textAlign: 'right', padding: '4px 8px', borderBottom: `1px solid ${T.border}` }}>Stam</th>
                              <th style={{ textAlign: 'right', padding: '4px 8px', borderBottom: `1px solid ${T.border}` }}>Uttag/år</th>
                            </tr>
                          </thead>
                          <tbody>
                            {econ.harvest.map(h => (
                              <tr key={h.spKey}>
                                <td style={{ padding: '4px 8px', color: T.text }}>{h.spLabel}</td>
                                <td style={{ padding: '4px 8px', textAlign: 'right', color: T.textSub }}>{h.stam}</td>
                                <td style={{ padding: '4px 8px', textAlign: 'right', color: T.amber, fontWeight: 700 }}>{h.uttakTot}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: 18 }}>
                    <label style={{ display: 'block', color: T.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Ditt namn (signatur) *</label>
                    <input value={formSignName} onChange={e => setFormSignName(e.target.value)} placeholder="För- och efternamn" style={{ width: '100%', boxSizing: 'border-box', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, padding: '10px 12px', color: T.text, fontSize: 14, fontFamily: 'inherit' }} />
                  </div>

                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginBottom: 20 }}>
                    <input type="checkbox" checked={formAccepted} onChange={e => setFormAccepted(e.target.checked)} style={{ accentColor: T.amber, width: 18, height: 18, marginTop: 2, flexShrink: 0 }} />
                    <span style={{ color: T.textSub, fontSize: 13, lineHeight: 1.6 }}>
                      Jag har läst och godkänner ovanstående villkor. Jag bekräftar att jag har behörighet att ingå detta avtal och att uppgifterna om marken är korrekta.
                    </span>
                  </label>

                  {formErr && <div style={{ background: 'rgba(192,57,43,0.12)', border: '1px solid rgba(192,57,43,0.3)', borderRadius: 6, padding: '10px 14px', color: '#ff6b6b', fontSize: 13, marginBottom: 14 }}>{formErr}</div>}

                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <button onClick={() => { setContractStep('form'); setFormErr(''); }} style={{ ...navBtnBase, background: 'none', border: `1px solid ${T.border}`, color: T.textSub }}>← Tillbaka</button>
                    <button
                      disabled={formSubmitting || !formAccepted || !formSignName.trim()}
                      onClick={async () => {
                        if (!formAccepted) { setFormErr('Du måste godkänna villkoren'); return; }
                        if (!formSignName.trim()) { setFormErr('Fyll i ditt namn'); return; }
                        setFormErr('');
                        setFormSubmitting(true);
                        try {
                          const avskjutningsplan = econ.harvest.map(h => ({
                            art: h.spLabel, artKey: h.spKey, stam: h.stam, uttakTot: h.uttakTot,
                            fallKr: h.subcats.length === 0 ? (speciesFall[h.spKey] ?? 0) : undefined,
                            subcats: h.subcats.map(s => ({ key: s.key, label: s.label, antal: s.antal, fallKr: s.fallKr })),
                          }));
                          const calculatorSnapshot = {
                            areaHa, county, counts, speciesFall, subcatFall, meatKgPris,
                            offerDrev, offerAlg, offerSmyg, offerAtel, offerSkogsfagel, offerFasan,
                            drevPerAr, jagarePerDrev, smygdagar, atelkvallar,
                            prisDrev, prisAlg, prisSmyg, prisAtel, prisFagel,
                            meatPolicy, equipment, brutto: econ.brutto, netto: econ.netto,
                          };
                          const contract = await api.submitMarkOwnerApplication({
                            markName: formMarkName.trim(),
                            fastighetsBeteckning: formFastighets.trim() || undefined,
                            areaHa,
                            county: county || undefined,
                            municipality: formMunicipality.trim() || undefined,
                            contactPhone: formPhone.trim() || undefined,
                            signatureName: formSignName.trim(),
                            avskjutningsplanJson: JSON.stringify(avskjutningsplan),
                            calculatorJson: JSON.stringify(calculatorSnapshot),
                          });
                          setSubmittedContract(contract);
                          setContractStep('done');
                          onMarkCreated?.();
                        } catch (e) {
                          setFormErr(e instanceof Error ? e.message : 'Något gick fel, försök igen.');
                        } finally {
                          setFormSubmitting(false);
                        }
                      }}
                      style={{ ...navBtnBase, background: T.amber, color: '#000', cursor: (formSubmitting || !formAccepted || !formSignName.trim()) ? 'not-allowed' : 'pointer', opacity: (formSubmitting || !formAccepted || !formSignName.trim()) ? 0.6 : 1 }}>
                      {formSubmitting ? 'Signerar...' : '✍ Signera och skicka ansökan'}
                    </button>
                  </div>
                  <div style={{ marginTop: 14, fontSize: 11, color: T.textMuted, lineHeight: 1.6 }}>
                    <strong style={{ color: T.text }}>OBS:</strong> Kalkylen är ett illustrativt hjälpmedel. Faktisk intäkt beror på avtal, bokningsgrad och lokala förutsättningar.
                  </div>
                </Card>
              )
            ) : (
              <>
                <Card T={T} title="Nästa steg — logga in eller kontakta oss">
                  <p style={{ color: T.textSub, fontSize: 14, lineHeight: 1.7, margin: '0 0 16px' }}>
                    Logga in för att registrera din mark digitalt och signera avtal direkt i plattformen. Eller kontakta oss så hjälper vi dig.
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 28 }}>
                    <a href="tel:+46101234567" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: T.amber, color: '#000', textDecoration: 'none', padding: '14px 28px', borderRadius: 8, fontWeight: 800, fontSize: 15, boxShadow: '0 4px 16px rgba(238,143,9,0.35)' }}>010-123 45 67</a>
                    <a href="mailto:mark@qvenari.se" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, border: `2px solid ${T.amber}`, color: T.amber, textDecoration: 'none', padding: '14px 28px', borderRadius: 8, fontWeight: 800, fontSize: 15, background: 'transparent' }}>mark@qvenari.se</a>
                  </div>
                  <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: '18px 20px' }}>
                    <div style={{ color: T.text, fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Ha gärna detta till hands:</div>
                    <ul style={{ color: T.textSub, fontSize: 13, lineHeight: 2.1, margin: 0, paddingLeft: 20 }}>
                      <li>Markens namn och fastighetsbeteckning</li>
                      <li>Areal i hektar och ungefärlig viltstam per art</li>
                      <li>Vilka jaktformer du vill erbjuda</li>
                      <li>Ditt personnummer / organisationsnummer</li>
                    </ul>
                  </div>
                  <div style={{ marginTop: 20, fontSize: 11, color: T.textMuted, lineHeight: 1.6, padding: '12px 16px', background: T.bg, borderRadius: 6, border: `1px solid ${T.border}` }}>
                    <strong style={{ color: T.text }}>OBS:</strong> Intäktskalkylen är ett illustrativt hjälpmedel. Faktisk intäkt beror på avtal, lokala förutsättningar och gällande jaktregler.
                  </div>
                </Card>
                <WizardNav onBack={() => setWizardStep(4)} />
              </>
            )}
          </>
        )}

      </div>
    </div>
  );
}
